import React, { useState, useEffect } from "react";
import { HiXMark } from "react-icons/hi2";
import {
  ActivityType,
  ActivityVariable,
  ActivityVariableDefinition,
} from "../../types/activity";
import { ActivityMeasurement } from "../../types/post";
import {
  normalizeActivityVariables,
  getUnitsForVariable,
} from "../../utils/unitUtils";
import {
  parseTimeStringToSeconds,
  formatSecondsToTimeString,
  unitToSeconds,
  secondsToUnitValue,
} from "../../utils/timeDurationUtils";

interface EditActivityMeasurementModalProps {
  isOpen: boolean;
  onClose: () => void;
  activityType: ActivityType;
  measurement: ActivityMeasurement;
  measurementIndex: number;
  onMeasurementUpdated: (
    measurementIndex: number,
    updatedMeasurement: Partial<ActivityMeasurement>,
  ) => void;
}

const EditActivityMeasurementModal: React.FC<
  EditActivityMeasurementModalProps
> = ({
  isOpen,
  onClose,
  activityType,
  measurement,
  measurementIndex,
  onMeasurementUpdated,
}) => {
  // Form state - variable values
  const [variableValues, setVariableValues] = useState<Record<string, string>>(
    {},
  );
  const [selectedUnits, setSelectedUnits] = useState<Record<string, string>>(
    {},
  );
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [activeUnitDropdown, setActiveUnitDropdown] = useState<string | null>(
    null,
  );

  // Normalize variables to handle both legacy and new formats
  const normalizedVariables = normalizeActivityVariables(
    activityType.variables,
  );

  // A variable is hidden if the coach flagged it on the stored measurement
  // variable, or (when there is no stored variable) on the activity-type def.
  const isVariableHidden = (variableName: string): boolean => {
    const measurementVariable = measurement?.variables.find(
      (v) => v.name === variableName,
    );
    if (measurementVariable?.hidden !== undefined)
      return !!measurementVariable.hidden;
    const def = normalizedVariables.find((v) => v.name === variableName);
    return !!def?.hidden;
  };

  // Check if a variable is a time variable
  const isTimeVariable = (variableName: string): boolean => {
    return variableName.toLowerCase() === "time";
  };

  // Rest gets the same dynamic entry as Time, but (unlike Time) it stores a
  // measurement + unit pair, so it needs converting through that unit rather
  // than always being raw seconds.
  const isRestVariable = (variableName: string): boolean => {
    return variableName.toLowerCase() === "rest";
  };

  // Dynamic athletics-style time entry: "30.30" (seconds), "1:08.90" (m:s),
  // "2:30:32" (h:m:s) - shape is read from the string, not fixed fields.
  const secondsToTimeDisplay = formatSecondsToTimeString;
  const timeDisplayToSeconds = parseTimeStringToSeconds;

  // Initialize form with measurement data
  useEffect(() => {
    if (isOpen && measurement) {
      // Initialize variable values from measurement or from activity type variables
      const initialValues: Record<string, string> = {};
      const initialUnits: Record<string, string> = {};

      normalizedVariables.forEach((variable) => {
        // Find the corresponding variable in the measurement
        const measurementVariable = measurement.variables.find(
          (v) => v.name === variable.name,
        );
        // Use existing unit if available, otherwise use default from activity type
        const resolvedUnit =
          measurementVariable?.unit || variable.defaultUnit;
        initialUnits[variable.name] = resolvedUnit;

        if (
          measurementVariable?.measurement !== null &&
          measurementVariable?.measurement !== undefined
        ) {
          if (isTimeVariable(variable.name)) {
            initialValues[variable.name] = secondsToTimeDisplay(
              measurementVariable.measurement,
            );
          } else if (isRestVariable(variable.name)) {
            initialValues[variable.name] = secondsToTimeDisplay(
              unitToSeconds(measurementVariable.measurement, resolvedUnit),
            );
          } else {
            initialValues[variable.name] =
              measurementVariable.measurement.toString();
          }
        } else {
          initialValues[variable.name] = "";
        }
      });

      setVariableValues(initialValues);
      setSelectedUnits(initialUnits);
      setError(null);
      setIsSubmitting(false);
    }
  }, [isOpen, measurement, activityType.variables]);

  const handleVariableChange = (variableName: string, value: string) => {
    setVariableValues((prev) => ({
      ...prev,
      [variableName]: value,
    }));
    setError(null);
  };

  const handleUnitChange = (variableName: string, unit: string) => {
    setSelectedUnits((prev) => ({
      ...prev,
      [variableName]: unit,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Build variables array with measurements and units
    const variables: ActivityVariable[] = normalizedVariables.map(
      (variable) => {
        const measurementVariable = measurement?.variables.find(
          (v) => v.name === variable.name,
        );

        // Preserve coach-hidden variables as-is so the configuration round-trips
        // (the athlete never sees or edits them).
        if (isVariableHidden(variable.name)) {
          return {
            name: variable.name,
            measurement: measurementVariable?.measurement ?? null,
            unit: measurementVariable?.unit ?? selectedUnits[variable.name],
            hidden: true,
          };
        }

        const value = variableValues[variable.name] || "";
        let measurementValue: number | null;
        if (isTimeVariable(variable.name)) {
          measurementValue = timeDisplayToSeconds(value);
        } else if (isRestVariable(variable.name)) {
          measurementValue = secondsToUnitValue(
            timeDisplayToSeconds(value),
            selectedUnits[variable.name],
          );
        } else {
          measurementValue = value.trim() ? parseFloat(value) : null;
          if (measurementValue !== null && isNaN(measurementValue))
            measurementValue = null;
        }

        return {
          name: variable.name,
          measurement: measurementValue,
          unit: selectedUnits[variable.name], // Include selected unit
        };
      },
    );

    // Check if at least one variable has a value
    const hasAtLeastOneValue = variables.some((v) => v.measurement !== null);
    if (!hasAtLeastOneValue) {
      setError("Please enter at least one measurement");
      return;
    }

    // Validate all entered values are valid numbers
    for (const variable of variables) {
      if (
        variableValues[variable.name].trim() !== "" &&
        variable.measurement === null
      ) {
        setError(`Please enter a valid number for ${variable.name}`);
        return;
      }
      if (variable.measurement !== null && variable.measurement < 0) {
        setError(`${variable.name} must be a positive number`);
        return;
      }
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const updatedMeasurement: Partial<ActivityMeasurement> = {
        variables: variables,
      };

      onMeasurementUpdated(measurementIndex, updatedMeasurement);
      onClose();
    } catch (error) {
      console.error("Error updating measurement:", error);
      setError("Failed to update measurement");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-4">
      <div className="bg-base-100 rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-base-200">
          <h2 className="text-xl font-semibold text-base-content">
            Edit {activityType.name}
          </h2>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-2 text-base-content/50 hover:text-base-content/80 rounded-full hover:bg-base-200 transition-colors disabled:opacity-50"
          >
            <HiXMark className="text-xl" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            {/* Variable Inputs */}
            {normalizedVariables.map((variable) => {
              // Respect the coach's hidden flag — never show hidden variables
              if (isVariableHidden(variable.name)) return null;

              const availableUnits = getUnitsForVariable(variable.name);
              const currentUnit =
                selectedUnits[variable.name] || variable.defaultUnit;

              return (
                <div key={variable.name}>
                  <label
                    htmlFor={`variable-${variable.name}`}
                    className="block text-sm font-medium text-base-content/80 mb-2"
                  >
                    {variable.name}
                  </label>
                  <div className="flex gap-2">
                    <input
                      id={`variable-${variable.name}`}
                      type={
                        isTimeVariable(variable.name) ||
                        isRestVariable(variable.name)
                          ? "text"
                          : "number"
                      }
                      step={isTimeVariable(variable.name) ? undefined : "0.01"}
                      min={isTimeVariable(variable.name) ? undefined : "0"}
                      inputMode="decimal"
                      value={variableValues[variable.name] || ""}
                      onChange={(e) => {
                        if (
                          isTimeVariable(variable.name) ||
                          isRestVariable(variable.name)
                        ) {
                          const filtered = e.target.value.replace(
                            /[^0-9:.]/g,
                            "",
                          );
                          handleVariableChange(variable.name, filtered);
                        } else {
                          handleVariableChange(variable.name, e.target.value);
                        }
                      }}
                      onBlur={() => {
                        if (
                          isTimeVariable(variable.name) ||
                          isRestVariable(variable.name)
                        ) {
                          handleVariableChange(
                            variable.name,
                            secondsToTimeDisplay(
                              timeDisplayToSeconds(
                                variableValues[variable.name] ?? "",
                              ),
                            ),
                          );
                        }
                      }}
                      placeholder={
                        isTimeVariable(variable.name) ||
                        isRestVariable(variable.name)
                          ? "0:00.00"
                          : `Enter ${variable.name.toLowerCase()}`
                      }
                      className="input bg-base-200 text-[16px] sm:text-sm flex-1 focus:outline-none focus:ring-0 focus:border-transparent border-base-200 border-2 p-4 rounded-md"
                      disabled={isSubmitting}
                    />
                    {/* Unit Selector - hidden for Rest, which uses the dynamic field above instead */}
                    {!isRestVariable(variable.name) && (
                      <div className="relative">
                        <input
                          type="text"
                          value={currentUnit}
                          onChange={(e) =>
                            handleUnitChange(variable.name, e.target.value)
                          }
                          onFocus={() => setActiveUnitDropdown(variable.name)}
                          onBlur={() => {
                            // Delay to allow click on dropdown item
                            setTimeout(() => setActiveUnitDropdown(null), 200);
                          }}
                          placeholder="unit"
                          disabled={isSubmitting}
                          className="w-full px-3 py-2 border-2 border-base-200 rounded-md bg-base-200 text-[16px] sm:text-sm focus:outline-none focus:ring-0 focus:border-transparent"
                        />

                        {/* Dropdown menu */}
                        {activeUnitDropdown === variable.name &&
                          availableUnits.length > 0 && (
                            <div className="absolute z-50 w-full mt-1 bg-base-100 border border-base-300 rounded-md shadow-lg max-h-40 overflow-y-auto">
                              {availableUnits.map((unit) => (
                                <button
                                  key={unit}
                                  type="button"
                                  onClick={() => {
                                    handleUnitChange(variable.name, unit);
                                    setActiveUnitDropdown(null);
                                  }}
                                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-base-200 focus:bg-base-200 focus:outline-none font-grotesk"
                                >
                                  {unit}
                                </button>
                              ))}
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Error Message */}
            {error && (
              <div className="text-red-600 text-sm bg-red-500/10 border border-red-200 rounded-lg p-2">
                {error}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-base-content/80 bg-base-100 border border-base-300 rounded-lg hover:bg-base-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 text-sm font-medium text-white bg-neutral rounded-lg hover:bg-neutral transition-colors disabled:bg-base-300 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Updating..." : "Update Measurement"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditActivityMeasurementModal;
